import React, { useMemo } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { StripePaymentElement, StripePaymentElementProps } from './StripePaymentElement';

/**
 * Wrapper component that provides the Stripe Elements context.
 *
 * The @stripe/react-stripe-js hooks (useStripe, useElements) require
 * being rendered inside an <Elements> provider. This wrapper handles
 * initializing Stripe and providing the context.
 */
interface StripePaymentElementWrapperProps extends Omit<StripePaymentElementProps, 'clientSecret'> {
  clientSecret: string;
}

let stripePromise: Promise<Stripe | null> | null = null;

const getStripePromise = () => {
  if (!stripePromise) {
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      console.error('VITE_STRIPE_PUBLISHABLE_KEY is not set');
      return Promise.resolve(null);
    }
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

export const StripePaymentElementWrapper: React.FC<StripePaymentElementWrapperProps> = (props) => {
  const stripePromise = useMemo(() => getStripePromise(), []);

  // Card Elements don't need clientSecret in the Elements provider
  // clientSecret is only used at confirmCardPayment time
  return (
    <Elements stripe={stripePromise} options={{ clientSecret: props.clientSecret }}>
      <StripePaymentElement {...props} />
    </Elements>
  );
};

export default StripePaymentElementWrapper;
